import { FREE_GIFT_CLASSES } from '@/components/blocks/FreeGiftV2/constants';
import { useFreeGiftInteraction } from '@/components/blocks/FreeGiftV2/context/FreeGiftInteractionContext';
import { MultiPromosSx } from '@/components/blocks/FreeGiftV2/styles/freeGift';
import { PromotionCombination } from '@/components/blocks/FreeGiftV2/types';
import {
	generateImageUrl,
	getPurchasesQuantities,
	getTargetQuantity,
} from '@/components/blocks/FreeGiftV2/utils';
import { Img } from '@/components/blocks/MaterialImage';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

const MAX_VISIBLE_THUMBNAILS = 4;

const resolvePromotionText = (
	combinable: boolean,
	qty: number,
	texts: {
		combinablePlural: string;
		combinable: string;
		fixedPlural: string;
		fixed: string;
	}
) => {
	if (combinable) {
		return qty > 1 ? texts.combinablePlural : texts.combinable;
	}
	return qty > 1 ? texts.fixedPlural : texts.fixed;
};

export type PromotionGroupCardProps = {
	group: PromotionCombination;
	currentPartNumber: string;
	onSelect: () => void;
};

export const PromotionGroupCard = ({
	group,
	currentPartNumber,
	onSelect,
}: PromotionGroupCardProps) => {
	const labels = useLocalization('FreeGift');
	const theme = useTheme();
	const { isRewardView } = useFreeGiftInteraction();

	const { rewardsQty } = getTargetQuantity(group.reward);
	const { parentQty, othersQty } = getPurchasesQuantities(group.purchase, currentPartNumber);
	const requiredBuyQty = parentQty + othersQty;
	const purchaseSkus = group.purchase.purchases.flatMap((purchase) => purchase.triggerSKUs);
	const rewardSkus = group.reward.rewards.flatMap((reward) => reward.targetSKUs);
	const promotionImages = isRewardView ? purchaseSkus : rewardSkus;
	const remainingCount = promotionImages.length - MAX_VISIBLE_THUMBNAILS;

	const purchaseText = resolvePromotionText(group.purchase.combinable, requiredBuyQty, {
		combinablePlural: labels.PurchaseTextCombinablePlural.t({ n: requiredBuyQty }),
		combinable: labels.PurchaseTextCombinable.t(),
		fixedPlural: labels.PurchaseTextFixedPlural.t({ n: requiredBuyQty }),
		fixed: labels.PurchaseTextFixed.t(),
	});

	const giftText = resolvePromotionText(group.reward.combinable, rewardsQty, {
		combinablePlural: labels.GiftTextCombinablePlural.t({ n: rewardsQty }),
		combinable: labels.GiftTextCombinable.t(),
		fixedPlural: labels.GiftTextFixedPlural.t({ n: rewardsQty }),
		fixed: labels.GiftTextFixed.t(),
	});

	return (
		<Box
			sx={MultiPromosSx.promoContainer}
			className={FREE_GIFT_CLASSES.promoCard}
			onClick={onSelect}
		>
			<Stack display="flex" width="100%" gap="15px">
				<Typography
					variant="bodySubText"
					color="grey.500"
					className={FREE_GIFT_CLASSES.promoCardTitle}
				>
					{purchaseText} {giftText}
				</Typography>
				<Box display="flex" className={FREE_GIFT_CLASSES.promoCardThumbnails}>
					{promotionImages.slice(0, MAX_VISIBLE_THUMBNAILS).map(({ sku }) => (
						<Img key={sku} src={generateImageUrl(sku, true)} alt={sku} sx={MultiPromosSx.image} />
					))}
					{remainingCount > 0 ? (
						<Box
							display="flex"
							width="60px"
							height="60px"
							alignItems="center"
							justifyContent="center"
						>
							<Typography
								variant="headingSubtitle"
								color="grey.500"
								className={FREE_GIFT_CLASSES.promoCardTitle}
								aria-label={`+${remainingCount}`}
							>
								{`+${remainingCount}`}
							</Typography>
						</Box>
					) : null}
				</Box>
			</Stack>
			<Box alignSelf="center" className={FREE_GIFT_CLASSES.promoCardChevron}>
				<DynamicIcon
					name={MATERIAL_SYMBOL_ICONS.chevronRight}
					variant="rounded"
					color={theme.palette.grey[500]}
					size="24px"
				/>
			</Box>
		</Box>
	);
};
